import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { actionsKeys, cn, formatAmount, permissionActions } from "@/lib/utils";
import { setBudgetData, setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useDeleteBudgetMutation,
  useGetBudgetByCommunityData,
  useGetBomUsersList,
  useGetCommunityApprovalInfo,
  useRevisedBudgetMutation,
  useSubmitForApprovalMutation,
  useGetCommunitiesDropdown,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Edit,
  FileText,
  MoreVertical,
  RotateCw,
  Trash2,
} from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import {
  ALL_STATUSES,
  ALL_YEARS,
  BUDGET_VIEW_TYPES,
  budgetSummaryConfig,
  getBudgetStatusGroup,
  getBudgetStatusKey,
  status,
  statusColor,
  statusIcon,
} from "./consts";

const useBudget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedYear, setSelectedYear] = useState(ALL_YEARS);
  const [selectedStatus, setSelectedStatus] = useState(ALL_STATUSES);
  const [viewType, setViewType] = useState(BUDGET_VIEW_TYPES.CARD);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowSelected, setRowSelected] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [showCopyDialog, setShowCopyDialog] = useState<boolean>(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState<boolean>(false);
  const [openReviseDialog, setOpenReviseDialog] = useState<boolean>(false);
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);

  const breadcrumbData = isMasterDataRoute ? undefined : [
    {
      link: `/${routes.bom}/${routes.finance}`,
      name: `Activity Dashboard`,
    },
    {
      link: "",
      name: <span className="text-primary">{"Budget Management"}</span>,
    },
  ];

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );
  const communityId = communityDetail?.communityId;

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunitiesDropdown({});

  useEffect(() => {
    if (communityList?.data) {
      setCommunityKey(Math.random() * 3);
    }
  }, [communityList]);

  const { data: budgetData, isLoading: isBudgetDataLoading } =
    useGetBudgetByCommunityData({
      communityId: communityId as string,
      queryParams: {
        enabled: !!communityId,
      },
    });

  const { data: bomUsersList } = useGetBomUsersList({
    communityId: communityId as string,
    queryParams: {
      enabled: !!communityId,
    },
  });

  const { data: communityApprovalInfo } = useGetCommunityApprovalInfo({
    community_id: communityId as string,
    queryParams: {
      enabled: !!communityId,
    },
  });

  const budgetAccessPermission = useMemo(
    () => accessPermission?.[actionsKeys.BUDGET?.toLowerCase()],
    [accessPermission]
  );

  const canAccessBudgetAction = (action: string) =>
    !!budgetAccessPermission?.[action];

  const canAddBudget = useMemo(
    () => !!budgetAccessPermission?.[permissionActions.CREATE],
    [budgetAccessPermission]
  );

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteBudgetMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenDeleteDialog(false);
        setRowSelected(null);
        toast.success("Budget Deleted Successfully");
      },
    });

  const { mutate: submitApproval, isPending: isSubmitApprovalPending } =
    useSubmitForApprovalMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenApprovalDialog(false);
        setRowSelected(null);
        toast.success("Budget Submitted for Approval");
      },
    });

  const { mutate: revisedBudget, isPending: isRevisedBudgetPending } =
    useRevisedBudgetMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenReviseDialog(false);
        setRowSelected(null);
        toast.success("Budget Revised Successfully");
      },
    });

  const budgetList = useMemo(
    () =>
      [...(budgetData?.data || [])].sort(
        (a: any, b: any) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [budgetData]
  );

  const yearOptions = useMemo(() => {
    const years = Array.from(
      new Set(
        budgetList
          ?.map((data: any) => data?.year?.toString())
          ?.filter((year: string) => !!year)
      )
    ).sort((a: any, b: any) => Number(b) - Number(a));

    return [
      { label: "All", value: ALL_YEARS },
      ...years.map((year) => ({ label: year, value: year })),
    ];
  }, [budgetList]);

  const budgetSummaryData = useMemo(() => {
    return budgetSummaryConfig.map((summaryData: any) => {
      const value = budgetList.filter(
        (budget: any) => getBudgetStatusGroup(budget?.status) == summaryData.key
      ).length;

      return {
        ...summaryData,
        value,
      };
    });
  }, [budgetList]);

  const filteredBudgetData = useMemo(() => {
    return budgetList.filter((data: any) => {
      const statusKey = getBudgetStatusGroup(data?.status);
      const matchesSearch =
        !globalFilter?.trim() ||
        [data?.budgetName, data?.budgetId, data?.year].some((value) =>
          value
            ?.toString()
            ?.toLowerCase()
            ?.includes(globalFilter.trim().toLowerCase())
        );
      const matchesYear =
        selectedYear == ALL_YEARS || data?.year?.toString() == selectedYear;
      const matchesStatus =
        selectedStatus == ALL_STATUSES || statusKey == selectedStatus;

      return matchesSearch && matchesYear && matchesStatus;
    });
  }, [budgetList, globalFilter, selectedStatus, selectedYear]);

  const budgetCards = useMemo(
    () =>
      filteredBudgetData.map((data: any) => {
        const statusKey = getBudgetStatusGroup(data?.status);

        return {
          ...data,
          icon: statusIcon[statusKey] || statusIcon.default,
          statusKey,
          statusLabel:
            status[statusKey] ||
            status[getBudgetStatusKey(data?.status)] ||
            data?.status ||
            "-",
          totalBudget: formatAmount(data?.totalAnnualBudget || 0),
          submittedOn: data?.updatedAt
            ? moment(data.updatedAt).format("MM-DD-YYYY")
            : "-",
        };
      }),
    [filteredBudgetData]
  );

  const totalRecords = filteredBudgetData.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const currentPageBudgetCards = useMemo(
    () =>
      budgetCards.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
    [budgetCards, pageNumber, pageSize]
  );

  useEffect(() => {
    setPageNumber(0);
  }, [communityId, globalFilter, selectedStatus, selectedYear]);

  useEffect(() => {
    setPageNumber((page) => Math.min(page, Math.max(totalPages - 1, 0)));
  }, [totalPages]);

  const onClear = () => {
    setGlobalFilter("");
    setSelectedYear(ALL_YEARS);
    setSelectedStatus(ALL_STATUSES);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const onCardClick = (budgetId: string) => {
    navigate(`${budgetId}/review`);
  };

  const onAdd = () => {
    navigate(routes.add);
  };

  const onLineItemsClick = (data: any) => {
    dispatch(setBudgetData(data));
    navigate(`${data?.budgetId}/${routes.lineItems}`);
  };

  const onEditClick = (data: any) => {
    navigate(`${data?.budgetId}/edit`);
  };

  const onDeleteClick = (data: any) => {
    setRowSelected(data);
    setOpenDeleteDialog(true);
  };

  const onCopyClick = (data: any) => {
    setRowSelected(data);
    setShowCopyDialog(true);
  };

  const validateBudgetApproval = () => {
    const activeBomCount = (bomUsersList?.data || [])?.filter(
      (bom: any) => bom?.active
    ).length;
    const requiredApprovals = Number(
      communityApprovalInfo?.data?.budgetApprovals || 0
    );

    if (activeBomCount < requiredApprovals) {
      toast.error(
        "You can't submit this budget for approval because approvals are greater than active BM members."
      );
      return false;
    }

    return true;
  };

  const onSubmitApprovalClick = (data: any) => {
    if (Number(data?.lineItemsCount) > 0) {
      if (!validateBudgetApproval()) return;
      setRowSelected(data);
      setOpenApprovalDialog(true);
    } else {
      toast.error(
        "You can't submit this budget for approval because it has no line items."
      );
    }
  };

  const onReviseClick = (data: any) => {
    setRowSelected(data);
    setOpenReviseDialog(true);
  };

  const onDeleteConfirm = () => {
    deleteInfo({
      communityId: communityId as string,
      budgetId: rowSelected?.budgetId,
    });
  };

  const onSubmitApproval = () => {
    if (!validateBudgetApproval()) return;
    submitApproval({
      communityId: communityId as string,
      budgetId: rowSelected?.budgetId,
      reason: "Submitted for Approval",
    });
  };

  const onRevisedConfirm = () => {
    revisedBudget({
      communityId: communityId as string,
      budgetId: rowSelected?.budgetId,
    });
  };

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };

  const renderBudgetActions = (data: any) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-white border-0 shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        {data?.status?.toLowerCase() == "draft" && (
          <>
            {canAccessBudgetAction(permissionActions.UPDATE) && (
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEditClick(data);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            )}
            {canAccessBudgetAction(permissionActions.DELETE) && (
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDeleteClick(data);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            )}
          </>
        )}
        {canAccessBudgetAction(permissionActions.CREATE) && (
          <DropdownMenuItem
            className="flex items-center gap-2"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCopyClick(data);
            }}
          >
            <Copy className="w-4 h-4" />
            Copy
          </DropdownMenuItem>
        )}
        {canAccessBudgetAction(permissionActions.READ) && (
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onLineItemsClick(data);
              }}
            >
              <FileText className="w-4 h-4" />
              Line Items
            </DropdownMenuItem>
          )}
        {data?.status?.toLowerCase() == "draft" &&
        data?.versionNo == "0" &&
        canAccessBudgetAction(permissionActions.SUBMIT) ? (
          <DropdownMenuItem
            className="flex items-center gap-2"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onSubmitApprovalClick(data);
            }}
          >
            <Check className="w-4 h-4" />
            Submit For Approval
          </DropdownMenuItem>
        ) : (
          <></>
        )}
        {data?.status?.toLowerCase() == "approved" &&
        canAccessBudgetAction(permissionActions.UPDATE) ? (
          <DropdownMenuItem
            disabled={isRevisedBudgetPending}
            className="flex items-center gap-2"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onReviseClick(data);
            }}
          >
            <RotateCw className="w-4 h-4" />
            Revise Budget
          </DropdownMenuItem>
        ) : (
          <></>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const columns = [
    {
      accessorKey: "budgetId",
      header: "Budget ID",
      accessorFn: (row: any) => String(row.budgetId || ""),
    },
    {
      accessorKey: "budgetName",
      header: "Budget Name",
      accessorFn: (row: any) => String(row?.budgetName || ""),
    },
    {
      accessorKey: "year",
      header: (
        <div>
          Financial <br /> Year
        </div>
      ),
      accessorFn: (row: any) => String(row.year || ""),
    },
    {
      accessorKey: "versionNo",
      header: (
        <div>
          Version <br /> No.
        </div>
      ),
      accessorFn: (row: any) => String(row?.versionNo ?? ""),
    },
    {
      accessorKey: "totalBudget",
      header: (
        <div>
          Total <br /> Budget
        </div>
      ),
      accessorFn: (row: any) => String(row?.totalBudget || ""),
    },
    {
      accessorKey: "submittedOn",
      header: (
        <div>
          Submitted <br /> On
        </div>
      ),
      accessorFn: (row: any) => String(row?.submittedOn || ""),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            "rounded-full px-4 py-2",
            statusColor.badge[row.row.original.statusKey] ||
              statusColor.badge.default
          )}
        >
          {row.row.original.statusLabel}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => renderBudgetActions(row.row.original),
    },
  ];

  return {
    breadcrumbData,
    onBackClick,
    onCardClick,
    onAdd,
    budgetCards: currentPageBudgetCards,
    budgetTableData: budgetCards,
    columns,
    renderBudgetActions,
    budgetSummaryData,
    yearOptions,
    globalFilter,
    setGlobalFilter,
    selectedYear,
    setSelectedYear,
    selectedStatus,
    setSelectedStatus,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    viewType,
    setViewType,
    onClear,
    isBudgetDataLoading,
    canAddBudget,
    rowSelected,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    onDeleteConfirm,
    showCopyDialog,
    setShowCopyDialog,
    openApprovalDialog,
    setOpenApprovalDialog,
    isSubmitApprovalPending,
    onSubmitApproval,
    openReviseDialog,
    setOpenReviseDialog,
    onRevisedConfirm,
    isRevisedBudgetPending,
    isMasterDataRoute,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey,
  };
};

export default useBudget;
